<template>
  <div v-click-outside="clickOutsideConfig" @click="onClickDatePicker">
    <date-picker
      v-model="dateDatePicker"
      :today-btn="false"
      :clear-btn="false"
      class="date-picker-octa"
      :class="{ hidden: !showDatePicker }"
      :width="280"
      :style="dynamicColors"
      icon-control-left="icon-chevron-left"
      icon-control-right="icon-chevron-right"
      :limit-from="startDate"
      :limit-to="endDate"
    />

    <div v-if="datePickerValid.status" class="invalid-date">
      <span v-html="$sanitizeHtml(datePickerValid.message)" />
    </div>

    <input
      ref="dateInput"
      v-model="dateInput"
      v-mask="$t('mask.date.mask')"
      type="text"
      class="octadesk-chat-message-textarea"
      :placeholder="$t('conversation.footer.comment.replyDate')"
      :disabled="disabled"
      @keypress.enter="handleSendMessage"
      @keyup="handleKeypress"
      @click="showDatePicker = true"
    />
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'

  import vClickOutside from 'v-click-outside'

  import dayjs from 'dayjs'

  import customParseFormat from 'dayjs/plugin/customParseFormat'

  dayjs.extend(customParseFormat)

  export default {
    directives: {
      clickOutside: vClickOutside.directive
    },
    props: {
      disabled: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        dateDatePicker: null,
        dateInput: null,
        showDatePicker: false,
        startDate: dayjs().subtract(100, 'years').format('YYYY-MM-DD'),
        endDate: dayjs().add(100, 'years').format('YYYY-MM-DD'),
        clickOutsideConfig: {
          handler: this.handleCloseDatePicker,
          events: ['click'],
          isActive: true
        }
      }
    },
    computed: {
      ...mapGetters(['settings', 'datePickerValid']),
      dynamicColors() {
        return {
          '--button-active-bg': `rgba(${this.settings.bgColorRGBA})`,
          '--button-active-color': `rgba(${this.settings.fontColorRGBA})`
        }
      }
    },
    watch: {
      dateInput: function (val) {
        if (dayjs(val, this.$t('mask.date.formatShow')).isValid()) {
          const formateDate = dayjs(val, this.$t('mask.date.format')).format(
            'YYYY-MM-DD'
          )

          let format = this.$t('mask.date.formatShow').replace(/#/g, '9')

          let message = this.validateMessages(formateDate, format)

          if (message) {
            setTimeout(() => {
              this.toggleDatePickerValid({ status: true, message })

              this.showDatePicker = false
            }, 200)
          } else {
            this.$emit('input', val)

            this.toggleDatePickerValid({ status: false, message: '' })
          }
        } else {
          this.$emit('input', '')

          this.toggleDatePickerValid({ status: false, message: '' })
        }
      }
    },
    mounted() {
      this.$refs.dateInput.focus()
    },
    methods: {
      ...mapActions(['toggleDatePickerValid']),
      compareDates(date1, date2) {
        return date1 >= date2
      },
      validateMessages(formateDate, format) {
        if (!dayjs(this.dateInput, this.$t('mask.date.formatShow')).isValid()) {
          return this.$t('mask.date.invalid.format')
        } else if (formateDate < this.startDate) {
          return this.$t('mask.date.invalid.minimumStart', {
            date: dayjs(this.startDate, 'YYYY-MM-DD').format(format)
          })
        } else if (formateDate >= this.endDate) {
          return this.$t('mask.date.invalid.minimumEnd', {
            date: dayjs(this.endDate, 'YYYY-MM-DD').format(format)
          })
        } else {
          return ''
        }
      },
      validateDate() {
        const formateDate = dayjs(
          this.dateInput,
          this.$t('mask.date.format')
        ).format('YYYY-MM-DD')

        let format = this.$t('mask.date.formatShow').replace(/#/g, '9')

        let message = this.validateMessages(formateDate, format)

        if (!this.dateInput) {
          return false
        } else if (message !== '') {
          this.toggleDatePickerValid({ status: true, message })

          return false
        } else {
          this.toggleDatePickerValid({ status: false, message: '' })

          return true
        }
      },
      handleSendMessage() {
        this.handleCloseDatePicker()

        if (this.validateDate()) {
          this.$emit('enterKeypress', this.dateInput)
        }
      },
      handleCloseDatePicker() {
        this.showDatePicker = false
      },
      handleKeypress($event) {
        this.dateDatePicker = dayjs(
          $event.target.value,
          this.$t('mask.date.format')
        ).format('YYYY-MM-DD')

        const code = $event.keyCode || $event.which

        if (code !== 13) {
          this.showDatePicker = $event.target.value !== ''

          this.toggleDatePickerValid({ status: false, message: '' })
        }
      },
      onClickDatePicker($event) {
        const path = $event.composedPath ? $event.composedPath() : $event.path
        if (path) {
          const uivDatepicker = path.filter(
            item =>
              item.className !== undefined &&
              item.className.includes('uiv-datepicker')
          )

          if (
            $event.target.dataset.action === 'select' &&
            uivDatepicker.length >= 1
          ) {
            this.showDatePicker = false
            this.dateInput = dayjs(this.dateDatePicker, 'YYYY-MM-DD').format(
              'DD/MM/YYYY'
            )
            this.$refs.dateInput.focus()
          }
        }
      }
    }
  }
</script>

<style lang="scss">
  div.date-picker-octa {
    position: fixed;
    z-index: 10;
    bottom: 76px;
    left: 50%;
    transform: translate(-50%, 0px);
    box-shadow: 0px 2px 20px #00000026;
    border-radius: 15px;
    padding: 25px;
    transition: 0.5s;
    background: #ffffff;
    &:before {
      content: '';
      width: 15px;
      height: 15px;
      position: absolute;
      bottom: -7.5px;
      left: 50%;
      transform: translate(-50%, 0px) rotate(45deg);
      background-color: #ffffff;
    }
    table {
      table-layout: fixed;
      td,
      small {
        font-size: 12px !important;
      }
      td {
        padding: 0px;
        button,
        b {
          font-weight: normal;
          padding: 0px;
        }
        button {
          transition: 0.5s;
          width: 100%;
          height: 31px;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 50px;
          &:hover:not(.btn-primary) {
            background-color: #f7f7f7;
          }
          &.btn-primary {
            background-color: var(--button-active-bg);
            color: var(--button-active-color) !important;
          }
        }
      }
      thead {
        td {
          color: #7d8f9c;
        }
        tr {
          &:first-child td {
            padding-bottom: 8px;
            &:first-child button,
            &:last-child button {
              border-radius: 50%;
              i {
                font-size: 20px;
                transition: 0.5s;
              }
              &:hover i {
                color: var(--button-active-bg);
              }
            }
            &:nth-child(2) button {
              width: 100%;
              text-align: center;
              font-weight: normal;
            }
          }
          &:last-child td {
            text-transform: uppercase;
            padding-bottom: 15px;
          }
        }
      }
    }
    &.uiv-datepicker-date table tbody tr td button {
      color: #666;
      border-radius: 50%;
      span.text-muted {
        color: #0000004d;
      }
    }
    &.uiv-datepicker-month tbody tr td button,
    &.uiv-datepicker-year tbody tr td button {
      color: #7d8f9c;
      text-transform: uppercase;
    }
    &.hidden {
      opacity: 0;
      pointer-events: none;
    }
  }
  div.invalid-date {
    transition: 0.5s;
    z-index: 2;
    border-radius: 0px;
    display: flex !important;
    justify-content: flex-start;
    align-items: center;
    padding: 10px 15px;
    background: #fef4f6;
    position: absolute;
    top: -54px;
    left: -16px;
    width: calc(100% + 32px);
    box-shadow: 0px -2px 16px rgba(0, 0, 0, 0.08);
    span {
      width: 100%;
      text-align: center;
      color: #fe1b31;
      font-size: 12px;
    }
  }
</style>
